import React, { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import { useParams, useHistory, Link } from "react-router-dom";

import axios from "../../helpers/axios";
import { Layout, ScreenHeading } from "../../webtracking/components";
import { UserTypes } from "../commons";

const useEditUserScreen = () => {
  const { userId } = useParams();
  const history = useHistory();

  const [user, setUser] = useState(null);
  const [userSOS, setUserSOS] = useState([]);
  const [userBranches, setUserBranches] = useState([]);
  const [isLoading, setLoading] = useState(true);

  const [globalPermission, setGlobalPermission] = useState(false);
  const [branchesPermission, setBranchesPermission] = useState(false);

  const loadUser = (id) => {
    setLoading(true);

    axios
      .get(`/api/user/${id}/`)
      .then(({ data }) => setUser(data))
      .catch((error) => console.error(error))
      .then(() => setLoading(false));

    axios
      .get(`/api/user/${id}/sos/`)
      .then(({ data }) => {
        setUserSOS(data.sos);
        setGlobalPermission(data.global_view);
      });

    axios
      .get(`/api/user/${id}/branches/`)
      .then(({ data }) => {
        setUserBranches(data.branches);
        setBranchesPermission(data.branches_view);
      });
  };

  useEffect(() => {
    loadUser(userId);
  }, [userId]);

  const setUserAttr = (attr, value) => {
    const copy = Object.assign({}, user);
    copy[attr] = value;
    setUser(copy);
  };

  const autocompleteSOS = (inputValue) =>
    new Promise((resolve) => {
      const results = [];

      if (inputValue) {
        axios.get("/api/sos/autocomplete/?q=" + inputValue).then((resp) => {
          resp.data.map((item) =>
            results.push({
              value: item.sos,
              label: `${item.sos} ${item.name}`,
            })
          );

          resolve(results);
        });
      } else {
        resolve([{ value: 0, label: "Busca sos", isDisabled: true }]);
      }
    });

  const autocompleteBranches = (inputValue) =>
    new Promise((resolve) => {
      const results = [];

      if (inputValue) {
        axios.get("/api/branch/autocomplete/?q=" + inputValue).then((resp) => {
          resp.data.map((item) =>
            results.push({
              value: item.name,
              label: `${item.name} ${item.id}`,
            })
          );

          resolve(results);
        });
      } else {
        resolve([{ value: 0, label: "Buscar branches", isDisabled: true}]);
      }
    });

  const appendUserSOS = (target) => {
    if (userSOS.indexOf(target) !== -1) {
      return null;
    }

    const copy = [target];
    userSOS.forEach((sos) => copy.push(sos));
    setUserSOS(copy);
  };

  const removeUserSOS = (target) => {
    if (userSOS.indexOf(target) === -1) {
      return null;
    }

    setUserSOS(userSOS.filter((sos) => sos !== target));
  };

  const appendUserBranch = (target) => {
    if (userBranches.indexOf(target) !== -1) {
      return null;
    }

    const copy = [target];
    userBranches.forEach((branch) => copy.push(branch));
    setUserBranches(copy);
  };

  const removeUserBranch = (target) => {
    if (userBranches.indexOf(target) === -1) {
      return null;
    }

    setUserBranches(userBranches.filter((branch) => branch !== target));
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();

    axios.patch(`/api/user/${userId}/`, user);
    axios.post(`/api/user/${userId}/sos/`, { sos: userSOS, global_view: globalPermission });
    axios.post(`/api/user/${userId}/branches/`, { branches: userBranches, branches_view: branchesPermission });
    history.push("/users");
  };

  const setGlPermission = () => {
    if (globalPermission) {
      setGlobalPermission(false);
    } else {
      setGlobalPermission(true);
    }
  }

  const setBrPermission = () => {
    if (branchesPermission) {
      setBranchesPermission(false);
    } else {
      setBranchesPermission(true);
    }
  }

  return {
    user,
    setUserAttr,
    setGlobalPermission,
    globalPermission,
    setBranchesPermission,
    branchesPermission,
    setGlPermission,
    setBrPermission,

    userSOS,
    userBranches,
    autocompleteSOS,
    autocompleteBranches,

    appendUserBranch,
    removeUserBranch,
    appendUserSOS,
    removeUserSOS,

    isLoading,
    handleFormSubmit,
  };
};

const EditUserScreen = () => {
  const {
    user,
    setUserAttr,
    setGlobalPermission,
    globalPermission,
    setBranchesPermission,
    branchesPermission,
    setGlPermission,
    setBrPermission,

    userSOS,
    userBranches,
    autocompleteSOS,
    autocompleteBranches,

    appendUserBranch,
    removeUserBranch,
    appendUserSOS,
    removeUserSOS,

    handleFormSubmit,
  } = useEditUserScreen();

  return (
    <Layout>
      <ScreenHeading title="Editar usuario" />
      <div className="container has-background-light p-6 mt-5">
        {user ? (
          <form onSubmit={handleFormSubmit}>
            <div className="field is-horizontal">
              <div className="field-label is-normal">
                <label className="label">Nombre completo</label>
              </div>
              <div className="field-body">
                <div className="field">
                  <div className="control">
                    <input
                      type="text"
                      className="input"
                      value={user.first_name}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
                <div className="field">
                  <div className="control">
                    <input
                      type="text"
                      className="input"
                      value={user.last_name}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="field is-horizontal">
              <div className="field-label is-normal">
                <label className="label">Correo electrónico</label>
              </div>
              <div className="field-body">
                <div className="field is-expanded">
                  <div className="control">
                    <input
                      type="email"
                      className="input"
                      value={user.email}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="field is-horizontal">
              <div className="field-label is-normal">
                <label className="label">Tipo de usuario</label>
              </div>
              <div className="field-body">
                <div className="field is-narrow">
                  <div className="control">
                    <div className="select is-fullwidth">
                      <select
                        value={user.user_type}
                        onChange={(event) =>
                          setUserAttr("user_type", event.target.value)
                        }
                      >
                        {UserTypes.map((label, value) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="field is-horizontal">
                <div className="field-label is-normal">
                  <label className="label">Permisos SOS</label>
                </div>
                <div className="field-body">
                  <div className="field is-expanded">
                    <div className="control">
                      {globalPermission ? (
                        <input type="checkbox" onClick={setGlPermission} checked></input>
                      ) : (
                        <input type="checkbox" onClick={setGlPermission}></input>
                      )}
                      <label>&nbsp;Acceso a todos los proveedores</label>
                    </div>
                  </div>
                </div>
              </div>

            {globalPermission ? (
              <div className="field is-horizontal"></div>
            ) : (
              <div>
                <div className="field is-horizontal">
                  <div className="field-label is-normal"></div>
                  <div className="field-body">
                    <div className="field is-expanded">
                      <div className="control">
                        <AsyncSelect
                          loadOptions={autocompleteSOS}
                          cacheOptions
                          defaultOptions
                          placeholder="Buscar por SOS"
                          value={null}
                          onChange={(row) => appendUserSOS(row.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label"></label>
                  </div>
                  <div className="field-body">
                    <div className="field is-expanded">
                      <table className="table is-fullwidth">
                        <thead>
                          <tr>
                            <th>SOS</th>
                            <th style={{ width: "1px" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {userSOS.map((sos) => (
                            <tr key={sos}>
                              <td>{sos}</td>
                              <td>
                                <span
                                  className="has-text-link"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => removeUserSOS(sos)}
                                >
                                  Borrar
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <br></br>
              </div>
            )}

            <div className="field is-horizontal">
              <div className="field-label is-normal">
                <label className="label">Permisos Sucursales</label>
              </div>
              <div className="field-body">
                <div className="field is-expanded">
                  <div className="control">
                    {branchesPermission ? (
                      <input type="checkbox" onClick={setBrPermission} checked></input>
                    ) : (
                      <input type="checkbox" onClick={setBrPermission}></input>
                    )}
                    <label>&nbsp;Acceso a todas las sucursales</label>
                  </div>
                </div>
              </div>
            </div>

            {branchesPermission ? (
              <div className="field is-horizontal"></div>
            ) : (
              <div>
                <div className="field is-horizontal">
                  <div className="field-label is-normal"></div>
                  <div className="field-body">
                    <div className="field is-expanded">
                      <div className="control">
                        <AsyncSelect
                          loadOptions={autocompleteBranches}
                          cacheOptions
                          defaultOptions
                          placeholder="Buscar por Sucursales"
                          value={null}
                          onChange={(row) => appendUserBranch(row.label)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="field is-horizontal">
                  <div className="field-label is-normal">
                    <label className="label"></label>
                  </div>
                  <div className="field-body">
                    <div className="field is-expanded">
                      <table className="table is-fullwidth">
                        <thead>
                          <tr>
                            <th>Sucursales</th>
                            <th style={{ width: "1px" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {userBranches.map((branch) => (
                            <tr key={branch}>
                              <td>{branch}</td>
                              <td>
                                <span
                                  className="has-text-link"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => removeUserBranch(branch)}
                                >
                                  Borrar
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <br></br>
              </div>
            )}

            <div className="field is-horizontal">
              <div className="field-label"></div>
              <div className="field-body">
                <div className="field is-grouped">
                  <div className="control">
                    <button className="button is-primary">Guardar</button>
                  </div>
                  <div className="control">
                    <Link to="/users" className="button is-link is-light">
                      Cancelar
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </form>
        ) : null}
      </div>
    </Layout>
  );
};

export default EditUserScreen;
